import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigation, useSearchParams } from '@remix-run/react';
import { Animation } from '@fluentui/react-northstar';
import { pxArrayToRem, SearchableDropdown } from '@archipro-design/aria';
import { CustomLocation, Loader, SearchLine } from '@archipro-design/icons';
import { useCountryContext } from '@archipro-website/localisation';
import { FindPlaceApiProviderEnum, FindPlaceTypeEnum } from 'generated/graphql';
import type { DropdownItem } from '~/modules/enquiry/types';
import type { BasicPlace } from '~/modules/location/type';
import type { IUseCurrentLocationResult } from '~/modules/location/hook/use-current-location';
import type { SearchLocation } from '../../type/api';
import type { FilterStatesKey } from '../../type/filter-state';
import {
    formatLocationSearchFilterText,
    usePlaceFinder,
} from '~/modules/location/hook/use-place-finder';

export interface LocationFilter {
    kind: 'Location';
    filterKey: FilterStatesKey;
    subFilterKey?: string;
}

export interface LocationFilterState extends Pick<LocationFilter, 'kind'> {
    selectedLocation?: SearchLocation;
}

interface FilterLocationProps extends IUseCurrentLocationResult {
    filter: LocationFilter;
    state: LocationFilterState;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<LocationFilterState>,
        updateStates?: boolean,
    ) => void;
}

const FilterLocation = ({
    filter,
    state,
    updateFilter,
    currentLocationDisabled,
    currentLocation,
    getCurrentLocation,
    loading,
}: FilterLocationProps) => {
    const navigation = useNavigation();
    const [searchParams] = useSearchParams();
    const { code: countryCode } = useCountryContext();
    const { filterKey } = filter;
    const requiredUseCurrentLocation = useRef(false);
    const [searchQuery, setSearchQuery] = useState('');
    const { selectedLocation } = state;
    const { changeKeyword: findPlace, searchFilterOptions } = usePlaceFinder({
        type: FindPlaceTypeEnum.SearchFilter,
        limit: 10,
        apiProvider: FindPlaceApiProviderEnum.ServiceableArea,
    });

    useEffect(() => {
        if (loading) return;
        if (currentLocation && requiredUseCurrentLocation.current) {
            setSearchQuery(formatLocationSearchFilterText(currentLocation));
            updateFilter(filterKey, {
                kind: 'Location',
                selectedLocation: currentLocation,
            });
        }
        requiredUseCurrentLocation.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocation, filterKey, loading]);

    const handleUseCurrentLocation = useCallback(() => {
        if (currentLocation) {
            // don't update the filter state if it's the same location
            // it will cause updateFilter change
            if (
                currentLocation.suburb === selectedLocation?.suburb &&
                currentLocation.district === selectedLocation?.district &&
                currentLocation.city === selectedLocation?.city &&
                currentLocation.region === selectedLocation?.region &&
                currentLocation.state === selectedLocation?.state &&
                currentLocation.country === selectedLocation?.country
            ) {
                return;
            }
            setSearchQuery(formatLocationSearchFilterText(currentLocation));
            updateFilter(filterKey, {
                kind: 'Location',
                selectedLocation: currentLocation,
            });
        } else {
            if (!requiredUseCurrentLocation.current) {
                requiredUseCurrentLocation.current = true;
                getCurrentLocation();
            }
        }
    }, [
        currentLocation,
        filterKey,
        getCurrentLocation,
        updateFilter,
        selectedLocation,
    ]);

    const useCurrentLocation = searchParams.get('currentLocation') === '1';

    useEffect(() => {
        if (!useCurrentLocation) return;
        handleUseCurrentLocation();
    }, [useCurrentLocation, handleUseCurrentLocation]);

    return (
        <div className="mb-4 flex flex-col md:mb-0">
            <SearchableDropdown
                disabled={navigation.state === 'loading'}
                items={searchFilterOptions.map((option, i) => ({
                    ...option,
                    className: '[&_.ui-box]:text-4',
                    content: (
                        <div className="mt-0.5 text-3.5 text-gray-500">
                            {option.placeType}
                        </div>
                    ),
                    key: option.header + i,
                }))}
                searchQuery={searchQuery}
                clearable
                onSearchQueryChange={(_, data) => {
                    setSearchQuery(data.searchQuery ?? '');
                    findPlace(data.searchQuery);
                }}
                searchInput={{
                    placeholder:
                        countryCode === 'AU'
                            ? 'City or state'
                            : 'City or region',
                    disabled: loading && requiredUseCurrentLocation.current,
                }}
                variables={{
                    // Fix input left padding when item is selected on mobile.
                    searchInputPaddingMobile: selectedLocation
                        ? pxArrayToRem([11, 48, 11, 12])
                        : pxArrayToRem([11, 48]),
                }}
                onChange={(_, data) => {
                    let selectedPlace: DropdownItem<BasicPlace> | null = null;
                    if (data.value) {
                        selectedPlace = data.value as DropdownItem<BasicPlace>;
                    } else {
                        requiredUseCurrentLocation.current = false;
                    }
                    updateFilter(filterKey, {
                        kind: 'Location',
                        selectedLocation: selectedPlace?.value,
                    });
                }}
                toggleIndicator={
                    loading && requiredUseCurrentLocation.current ? (
                        <Animation name="rotateUltraSlow">
                            <Loader size="2x" />
                        </Animation>
                    ) : (
                        <SearchLine size="2x" />
                    )
                }
            />
            <button
                className="mt-4 flex items-center gap-1 self-start text-4 text-gray-700 hover:underline md:text-5"
                disabled={navigation.state === 'loading'}
                onClick={handleUseCurrentLocation}
            >
                <CustomLocation className="size-4 md:size-5" />
                <span>Use my current location</span>
            </button>
            {currentLocationDisabled && (
                <div className="mt-4 text-3.5 leading-tight text-[#d03b47] md:text-4">
                    Please change your browser settings to allow location
                    services.
                </div>
            )}
        </div>
    );
};

export default FilterLocation;
